import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useUserData, AppItem } from 'helper/UserData';

const SatuAplikasi = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { userApps, isLoading } = useUserData();
    const launcherRef = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const toggle = () => setDropdownOpen(prevState => !prevState);

    const listApp: Array<AppItem & { kategori?: string }> = useMemo(() => {
        if (Array.isArray(userApps)) {
            return userApps;
        }
        if (userApps && typeof userApps === 'object') {
            return Object.values(userApps).flat() as Array<AppItem & { kategori?: string }>;
        }
        return [];
    }, [userApps]);

    const categoryOrder = ['akademik', 'kemahasiswaan', 'kerjasama', 'keuangan', 'umum'];

    const groupedApps = useMemo(() => {
        return listApp.reduce((acc, app) => {
            const key = app.kategori || 'lainnya';
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(app);
            return acc;
        }, {} as Record<string, Array<AppItem & { kategori?: string }>>);
    }, [listApp]);

    const orderedCategories = useMemo(() => {
        const known = categoryOrder.filter(category => groupedApps[category]?.length);
        const extra = Object.keys(groupedApps)
            .filter(category => !categoryOrder.includes(category))
            .sort();
        return [...known, ...extra];
    }, [groupedApps]);

    const handleAppClick = (domain: string) => {
        // Buka aplikasi di tab baru
        window.open(`https://${domain}/authentication/satu_login`, '_blank');
        setDropdownOpen(false);
    };

    useEffect(() => {
        if (!dropdownOpen) {
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (launcherRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            setDropdownOpen(false);
        };

        const handleKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);
        document.addEventListener('keydown', handleKeydown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [dropdownOpen]);

    return (
        <li className="app-launcher-wrapper">
            <div
                id="app-launcher"
                ref={launcherRef}
                role="button"
                tabIndex={0}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="5" cy="5" r="2.5" />
                    <circle cx="12" cy="5" r="2.5" />
                    <circle cx="19" cy="5" r="2.5" />
                    <circle cx="5" cy="12" r="2.5" />
                    <circle cx="12" cy="12" r="2.5" />
                    <circle cx="19" cy="12" r="2.5" />
                    <circle cx="5" cy="19" r="2.5" />
                    <circle cx="12" cy="19" r="2.5" />
                    <circle cx="19" cy="19" r="2.5" />
                </svg>
            </div>

            <div
                id="app-dropdown-menu"
                ref={menuRef}
                className={`app-dropdown-menu ${dropdownOpen ? 'show' : ''}`}
            >
                <div className="app-dropdown-header">
                    <h6>Jelajahi Satu Unmul</h6>
                </div>
                <div className="app-dropdown-content">
                    {isLoading ? (
                        <div className="app-loader">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : listApp.length === 0 ? (
                        <div className="app-loader">
                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                Data aplikasi tidak tersedia.
                            </span>
                        </div>
                    ) : (
                        <div>
                            {orderedCategories.map((category) => (
                                <div className="app-category-section" key={category}>
                                    <div className="app-category-label">
                                        <h6>{category}</h6>
                                    </div>
                                    <div className="app-grid">
                                        {groupedApps[category].map((app, index) => (
                                            <button
                                                key={`${category}-${index}`}
                                                type="button"
                                                className="app-item"
                                                onClick={() => handleAppClick(app.domain)}
                                                title={app.nama || app.singkatan}
                                            >
                                                <div className="app-icon">
                                                    <Image
                                                        src={`https://cdn.unmul.ac.id/logo/${app.singkatan}.png`}
                                                        alt={`Logo ${app.singkatan}`}
                                                        width={36}
                                                        height={36}
                                                    />
                                                </div>
                                                <span className="app-name">{app.singkatan}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="app-view-all">
                    <a href="https://satu.unmul.ac.id" target="_blank" rel="noreferrer">
                        Buka Satu Unmul &rarr;
                    </a>
                </div>
            </div>
        </li>
    );
};

export default SatuAplikasi;